import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Stack,
  useTheme,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  TextareaAutosize,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton } from "@mui/material";
import { Collapse, Card, CardContent } from "@mui/material";
import ErrorDialog from "../common/ErrorDialog/ErrorDialog";
import { useArrayActions, useArrayData } from "../../context/ArrayDataContext";
import { Fab } from "@mui/material";
import { useState, useMemo, useEffect, useRef } from "react";
import { ColDef, themeQuartz } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { approveViewPostApi } from "../../api/approverViewPostApi";
import { ApproveWorkflowRequest } from "../../types/approveViewPostReqRes";
import VersionHistoryView from "../VersionHistory/VersionHistoryView";
import { formatOneDecimal, formatTwoDecimals, formatValueNoDecimals } from "../utils/formatTwoDecimals";
import { gridTheme } from "../../utils/tableUtils";
import { formControlStyle } from "../../utils/theme";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import { getReasonCodeList } from "../../api/reasonCodeApi";
import { getMaxCoverage, MaxCoverageLimit } from "../../api/maxCoverageApi";

const RejectPopUp = ({
  open,
  onClose,
  onSave,
  data,
  setToastOpen,
  approverId,
  fetchData,
}) => {
  const [reasonCode, setReasonCode] = useState(data?.reasonCode || "");
  const [comments, setComments] = useState("");
  const [expandedHistory, setExpandedHistory] = useState(true);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [errorToast, setErrorToast] = useState<{ open: boolean; msg: string }>({
    open: false,
    msg: "",
  });

  const userChoices = Array.isArray(data?.userSelectedChoice)
    ? data.userSelectedChoice
    : String(data?.userSelectedChoice || "")
      .split(",")
      .map((c) => c.trim());
  const isS4 = userChoices.includes("S4 Proposal");
  const isIO = userChoices.includes("IO Proposal");
  const isManual = userChoices.includes("New Proposal");
  const [decision, setDecision] = useState(""); // S4 / IO / Manual
  const [selectedRejectType, setSelectedRejectType] = useState<
    "planner" | "manual"
  >("planner");
  const theme = useTheme();
  const { upsertItem, removeItem, items } = useArrayData();
  const [submitting, setSubmitting] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [maxCoverage, setMaxCoverage] = useState<MaxCoverageLimit | null>(null);
  /** Real-time limit errors for the editable New Proposal cells */
  const [cellLimitErrors, setCellLimitErrors] = useState<{
    safetyTime?: string;
    coverageProfile?: string;
  }>({});
  const cellLimitErrorsRef = useRef<{
    safetyTime?: string;
    coverageProfile?: string;
  }>({});
  // Prefer raw values if available
  const raw = data?.__raw || data;
  const [rows, setRows] = useState<any[]>([
    {
      choice: "Accept End User Proposal",
      coverageProfile: raw["userCoverageProfileRaw"] ?? raw["userCoverageProfile"],
      safetyTime: raw["userSafetyTimeRaw"] ?? raw["userSafetyTime"],
      safetyStock: raw["userSafetyStockQtyRaw"] ?? raw["userSafetyStockQty"],
    },
    ...(isS4 || isManual
      ? [
        {
          choice: "Accept IO value",
          coverageProfile: raw["ioCoverageProfileRaw"] ?? raw["ioCoverageProfile"],
          safetyTime: raw["ioSafetyTimeRaw"] ?? raw["ioSafetyTime"],
          safetyStock: 0,
        },
      ]
      : []),

    ...(isIO || isManual
      ? [
        {
          choice: "Keep S4 values",
          coverageProfile: raw?.s4CoverageProfileRaw ?? raw?.s4CoverageProfile,
          safetyTime: raw?.s4SafetyTimeRaw ?? raw?.s4SafetyTime,
          safetyStock: raw?.s4SafetyStockQtyRaw ?? raw?.s4SafetyStockQty,
        },
      ]
      : []),

    {
      choice: "New Proposal",
      coverageProfile: "",
      safetyTime: "",
      safetyStock: "",
    },
  ]);
  const [selectedChoice, setSelectedChoice] = useState<any>(rows[0]);

  const parseNumberOrText = (value: any) => {
    if (value === null || value === undefined) return "";
    const v = String(value).trim();
    if (v === "-" || v === "") return "";
    const num = Number(v);
    return Number.isFinite(num) ? num : v; // allow text if needed
  };
  // Use the proper signature for Ag Grid valueParser
  const valueParserFromParams = (params) => {
    const v = String(params.newValue ?? "").trim();

    // Reject empty or "-" → return 0
    if (v === "" || v === "-") return 0;

    // Allow only digits or one decimal point
    const numericRegex = /^\d+(\.\d+)?$/;

    if (!numericRegex.test(v)) {
      return params.oldValue; // invalid -> keep previous value
    }

    return Number(v); // valid number -> convert to numeric
  };
  // Optional: show "-" when the value is empty
  const valueFormatterDashIfEmpty = (params: any) => {
    const v = params?.value;
    return v === "" || v === null || v === undefined ? "-" : v;
  };

  const toSafeNumber = (v: any) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const cleaned = String(v)
      .trim()
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");
    if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  // 🔥 ADD THIS — fetch reason list when popup opens
  useEffect(() => {
    if (!open) return;

    let alive = true;

    (async () => {
      try {
        setReasonLoading(true);
        setReasonError(null);

        const apiList = await getReasonCodeList();

        const mapped = (apiList ?? []).map((r: any) => ({
          value: String(r.reasonCode),
          label: String(r.reasonDescription),
        }));

        if (alive) setReasonOptions(mapped);
      } catch (err) {
        if (alive) setReasonError("Failed to load reasons");
      } finally {
        if (alive) setReasonLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open]);

  // Fetch max-coverage limits when popup opens
  useEffect(() => {
    if (!open) return;

    const plantId =
      data?.materialPlant ??
      data?.["Material Plant"] ??
      "";

    if (!plantId) return;

    let alive = true;
    (async () => {
      try {
        const limits = await getMaxCoverage(plantId);
        if (alive) setMaxCoverage(limits);
      } catch {
        if (alive) setMaxCoverage(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, data]);
  const RadioCellRenderer = (props: any) => {
    const checked =
      props.node.isSelected() || props.data.choice === selectedChoice?.choice;

    const onChange = () => {
      props.node.setSelected(true); // select AG Grid row
      setSelectedChoice(props.data); // update React state
    };

    return (
      <Radio
        checked={ checked }
        onChange={ onChange }
        value={ props.data.choice }
        size="small"
        sx={ {
          color: "#000",
          "&.Mui-checked": {
            color: "#000",
          },
        } }
      />
    );
  };

  const columns: ColDef[] = [
    {
      headerName: "",
      cellRenderer: RadioCellRenderer,
      width: 35,
      maxWidth: 35,
      cellStyle: {
        textAlign: "center",
        padding: "0 8px",
        pointerEvents: "auto",
      },
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      field: "choice",
      headerName: "Choice",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "coverageProfile",
      headerName: "Coverage Profile (weeks)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams,
      valueFormatter: ({ value }) => formatOneDecimal(value),
      cellStyle: (p) => {
        const base = { display: "flex", alignItems: "center", justifyContent: "center" };
        if (p.data?.choice === "New Proposal" && cellLimitErrorsRef.current.coverageProfile) {
          return { ...base, backgroundColor: "#ffebee", border: "1px solid #f44336" };
        }
        return base;
      },
    },

    {
      field: "safetyTime",
      headerName: "Safety Time(weeks)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams,
      valueFormatter: ({ value }) => formatOneDecimal(value),
      cellStyle: (p) => {
        const base = { display: "flex", alignItems: "center", justifyContent: "center" };
        if (p.data?.choice === "New Proposal" && cellLimitErrorsRef.current.safetyTime) {
          return { ...base, backgroundColor: "#ffebee", border: "1px solid #f44336" };
        }
        return base;
      },
    },
    {
      field: "safetyStock",
      headerName: "Safety Stock(qty)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams, //  correct
      valueFormatter: ({ value }) => formatValueNoDecimals(value),
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  ];

  // console.log('data in popup ', data)

  const handleToggleHistory = () => setExpandedHistory((prev) => !prev);

  const handleCellValueChanged = (params: any) => {
    const { data, colDef, newValue } = params;
    const field = colDef.field;

    // Auto-select "New Proposal" row when user edits any of its cells
    if (data.choice === "New Proposal") {
      params.api.getRowNode("New Proposal")?.setSelected(true, true);
    }

    setRows((prev) =>
      prev.map((r) =>
        r.choice === data.choice ? { ...r, [field]: newValue } : r,
      ),
    );

    // If editing New Proposal → always update selectedChoice; otherwise only if it was already selected
    setSelectedChoice((prev) =>
      data.choice === "New Proposal"
        ? { ...data, [field]: newValue }
        : prev?.choice === data.choice ? { ...prev, [field]: newValue } : prev,
    );

    // Real-time limit validation for editable New Proposal cells
    if (data.choice === "New Proposal" && maxCoverage) {
      const updated = { ...cellLimitErrorsRef.current };

      if (field === "safetyTime") {
        const val = toSafeNumber(newValue);
        updated.safetyTime =
          val > maxCoverage.safetyTime
            ? `Safety Time ${val} exceeds the plant limit of ${maxCoverage.safetyTime}`
            : undefined;
      }
      if (field === "coverageProfile") {
        const val = toSafeNumber(newValue);
        updated.coverageProfile =
          val > maxCoverage.coverageProfile
            ? `Coverage Profile ${val} exceeds the plant limit of ${maxCoverage.coverageProfile}`
            : undefined;
      }

      cellLimitErrorsRef.current = updated;
      setCellLimitErrors({ ...updated });
      params.api.refreshCells({ force: true, columns: ["safetyTime", "coverageProfile"] });
    }
  };

  function getVariationRows(data: any, selectedRow: any) {
    const raw = data?.__raw || data;
    console.log("data", data)
    const weeklyDemand = Number(parseNumberOrText(raw?.["weeklyDemandQtyRaw"])) || Number(parseNumberOrText(raw?.["weeklyDemandQty"])) || 0;
    const partUnitsPerEA = Number(parseNumberOrText(raw?.["partUnitsPerEaRaw"])) || Number(parseNumberOrText(raw?.["partUnitsPerEa"])) || 0;
    const pricePerUnit = Number(parseNumberOrText(raw?.["pricePerUnitRaw"])) || Number(parseNumberOrText(raw?.["pricePerUnit"])) || 0;
    const stdPricePerQty = pricePerUnit <= 0 ? 0 : (raw.standardPrice * raw.exchangeRate) / pricePerUnit;

    // Decide which safety stock values to use based on selection
    let safetyStockQty = 0;
    let coverageProfile = 0;
    let safetyTime = 0;

    switch (selectedRow?.choice) {
      case "Accept End User Proposal":
        safetyStockQty = Number(parseNumberOrText(raw?.["userSafetyStockQtyRaw"])) || Number(parseNumberOrText(raw?.["userSafetyStockQty"])) || 0;
        coverageProfile = Number(parseNumberOrText(raw?.["userCoverageProfileRaw"])) || Number(parseNumberOrText(raw?.["userCoverageProfile"])) || 0;
        safetyTime = Number(parseNumberOrText(raw?.["userSafetyTimeRaw"])) || Number(parseNumberOrText(raw?.["userSafetyTime"])) || 0;
        break;
      case "Accept IO value":
        safetyStockQty = 0;
        coverageProfile = Number(parseNumberOrText(raw?.["ioCoverageProfileRaw"])) || Number(parseNumberOrText(raw?.["ioCoverageProfile"])) || 0;
        safetyTime = Number(parseNumberOrText(raw?.["ioSafetyTimeRaw"])) || Number(parseNumberOrText(raw?.["ioSafetyTime"])) || 0;
        break;
      case "Keep S4 values":
        safetyStockQty = Number(parseNumberOrText(raw?.["s4SafetyStockQtyRaw"])) || Number(parseNumberOrText(raw?.["s4SafetyStockQty"])) || 0;
        coverageProfile = Number(parseNumberOrText(raw?.["s4CoverageProfileRaw"])) || Number(parseNumberOrText(raw?.["s4CoverageProfile"])) || 0;
        safetyTime = Number(parseNumberOrText(raw?.["s4SafetyTimeRaw"])) || Number(parseNumberOrText(raw?.["s4SafetyTime"])) || 0;
        break;
      case "New Proposal":
        safetyStockQty = Number(parseNumberOrText(selectedRow?.safetyStock)) || 0;
        coverageProfile = Number(parseNumberOrText(selectedRow?.coverageProfile)) || 0;
        safetyTime = Number(parseNumberOrText(selectedRow?.safetyTime)) || 0;
        break;
    }

    // Consolidated Safety Stock (wk) - Approved
    const consolidatedApproved =
      weeklyDemand === 0
        ? coverageProfile + safetyTime
        : safetyStockQty / weeklyDemand + coverageProfile + safetyTime;
    const ioConsolidatedSafetyStock = Number(parseNumberOrText(raw?.["ioConsolidatedSafetyStockRaw"])) || Number(parseNumberOrText(raw?.["ioConsolidatedSafetyStockRounded"])) || 0;
    const s4ConsolidatedSafetyStock = Number(parseNumberOrText(raw?.["s4ConsolidatedSafetyStockRaw"])) || Number(parseNumberOrText(raw?.["s4ConsolidatedSafetyStock"])) || 0;

    const varWKvsIO = consolidatedApproved - ioConsolidatedSafetyStock;

    const varWKvsS4 = consolidatedApproved - s4ConsolidatedSafetyStock;

    // Match ApproverView: use Math.round for percentage
    const varPctIO = ioConsolidatedSafetyStock === 0 ? "N/A" : Math.round((varWKvsIO / ioConsolidatedSafetyStock) * 100);
    const varPctS4 = s4ConsolidatedSafetyStock === 0 ? "N/A" : Math.round((varWKvsS4 / s4ConsolidatedSafetyStock) * 100);

    const varPdrIO = varWKvsIO * weeklyDemand * partUnitsPerEA;
    const varPdrS4 = varWKvsS4 * weeklyDemand * partUnitsPerEA;

    const varUsdIO = varWKvsIO * weeklyDemand * stdPricePerQty;
    const varUsdS4 = varWKvsS4 * weeklyDemand * stdPricePerQty;

    return [
      {
        metric: "Approved value vs IO",
        week: formatOneDecimal(varWKvsIO),
        percentage: varPctIO === "N/A" ? "N/A" : varPctIO + "%",
        pdr: formatValueNoDecimals(varPdrIO),
        value: "$" + formatValueNoDecimals(varUsdIO),
      },
      {
        metric: "Approved value vs S4",
        week: formatOneDecimal(varWKvsS4),
        percentage: varPctS4 === "N/A" ? "N/A" : varPctS4 + "%",
        pdr: formatValueNoDecimals(varPdrS4),
        value: "$" + formatValueNoDecimals(varUsdS4),
      },
    ];
  }

  const variationRows = useMemo(
    () => getVariationRows(data, selectedChoice),
    [data, selectedChoice],
  );

  const variationColumns: ColDef[] = [
    {
      field: "metric",
      headerName: "Metric",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "week",
      headerName: "Week",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatOneDecimal(value),
    },
    {
      field: "percentage",
      headerName: "Percentage",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatValueNoDecimals(value),
    }, // keep % string
    {
      field: "pdr",
      headerName: "PDR",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatValueNoDecimals(value),
    },
    {
      field: "value",
      headerName: "Value(USD)",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  ];

  const validateSelection = () => {
    if (!selectedChoice) {
      return "Please select a proposal option.";
    }

    if (
      (selectedChoice.choice === "Keep S4 values" ||
        selectedChoice.choice === "New Proposal") &&
      !reasonCode
    ) {
      return "Rejection reason is mandatory for S4 or New Proposal.";
    }

    return null;
  };

  const buildChoice = (choice) => {
    if (choice == "Accept IO value") {
      return "IO Proposal";
    } else if (choice == "Keep S4 values") {
      return "S4 Proposal";
    } else if (choice == "New Proposal") {
      return "Approver Proposal";
    } else if (choice == "Accept End User Proposal") {
      return "End User Proposal";
    }
  };

  const getReasonCode = (choice: any, reasonCode: any) => {
    if (choice === "End User Proposal") {
      return "End User SS Approved";
    }
    if (choice === "IO Proposal") {
      return "IO selected";
    } else {
      return reasonOptions?.find((r) => r.value === reasonCode)?.label ?? "";
    }
  };
  const buildSubmitPayload = (): ApproveWorkflowRequest[] => [
    {
      workflowId: data?.workflowId,
      approverId: data.approverId,
      approverSelectedChoice: buildChoice(selectedChoice.choice),
      approverComments: comments,
      approverReasonCode: getReasonCode(
        buildChoice(selectedChoice.choice),
        reasonCode,
      ),
      approvedSafetyStockQty: selectedChoice?.choice === "Accept IO value" ? 0
        : selectedChoice?.choice === "New Proposal"
          ? Math.round(toSafeNumber(selectedChoice?.safetyStock))
          : toSafeNumber(selectedChoice?.safetyStock),
      approvedCoverageProfile: selectedChoice?.choice === "New Proposal"
        ? parseFloat(toSafeNumber(selectedChoice?.coverageProfile).toFixed(1))
        : toSafeNumber(selectedChoice?.coverageProfile),
      approvedSafetyTime: selectedChoice?.choice === "New Proposal"
        ? parseFloat(toSafeNumber(selectedChoice?.safetyTime).toFixed(1))
        : toSafeNumber(selectedChoice?.safetyTime),
      approvedConsolidatedSafetyStock: toSafeNumber(
        data?.approvedConsolidatedSafetyStock,
      ),

      varWkApprovedSSvsS4: toSafeNumber(data?.varWkApprovedSSvsS4),
      varPctApprovedSSvsS4: String(toSafeNumber(data?.varPctApprovedSSvsS4)),

      consolidatedSafetyStockUsdApproved: toSafeNumber(
        data?.consolidatedSafetyStockUsdApproved,
      ),
      varUsdApprovedSSvsS4: String(toSafeNumber(data?.varUsdApprovedSSvsS4)),
      varPdrApprovedSSvsS4: toSafeNumber(data?.varPdrApprovedSSvsS4),
    },
  ];

  const handleSubmit = async () => {
    const errorMsg = validateSelection();
    if (errorMsg) {
      setErrorToast({ open: true, msg: errorMsg });
      return;
    }

    // Validate proposed values against plant max-coverage limits
    if (maxCoverage) {
      const proposedSafetyTime = parseFloat(
        toSafeNumber(selectedChoice?.safetyTime).toFixed(1),
      );
      const proposedCoverage = parseFloat(
        toSafeNumber(selectedChoice?.coverageProfile).toFixed(1),
      );
      const limitViolations: string[] = [];

      if (proposedSafetyTime > maxCoverage.safetyTime) {
        limitViolations.push(
          `Safety Time ${proposedSafetyTime} exceeds the plant limit of ${maxCoverage.safetyTime}`,
        );
      }
      if (proposedCoverage > maxCoverage.coverageProfile) {
        limitViolations.push(
          `Coverage Profile ${proposedCoverage} exceeds the plant limit of ${maxCoverage.coverageProfile}`,
        );
      }

      if (limitViolations.length > 0) {
        setErrorToast({
          open: true,
          msg: limitViolations.join("\n"),
        });
        return;
      }
    }

    const payload = buildSubmitPayload();
    const inProgressId = enqueueSnackbar({
      message: "Submitting materials for Approval",
      severity: "warning",
      persist: true,
    });
    try {
      onClose();
      setSubmitting(true); // Disable button
      const response = await approveViewPostApi(payload);
      setToastOpen(true);
      enqueueSnackbar({
        message: "Approval Captured Successfully",
        severity: "success",
        autoHideDuration: 2500,
      });
      fetchData();
    } catch (error) {
      console.error("Submission failed:", error);
      enqueueSnackbar({
        message: `Failed to Submit the Material`,
        severity: "error",
        autoHideDuration: 2500,
      });
    } finally {
      setSubmitting(false); // Re-enable button after API finishes
      closeSnackbar(inProgressId);
    }
  };

  const getDataId = (item) => {
    const id = String(item?.materialNumber ?? item?.id ?? "");
    return id;
  };
  const isMissing = (v: any) =>
    v === "" || v === "-" || v === null || v === undefined;
  const isIncomplete = () => {
    const choice = selectedChoice?.choice;

    // S4 rejection → Rejection Reason required
    if (choice === "Keep S4 values") {
      return isMissing(reasonCode);
    }

    // New Proposal → all fields + reason required
    if (choice === "New Proposal") {
      if (isMissing(selectedChoice?.safetyStock)) return true;
      if (isMissing(selectedChoice?.coverageProfile)) return true;
      if (isMissing(selectedChoice?.safetyTime)) return true;
      if (isMissing(reasonCode)) return true;
    }

    return false;
  };
  const getMissingMessage = () => {
    const choice = selectedChoice?.choice;
    const missing: string[] = [];

    if (choice === "Keep S4 values") {
      if (isMissing(reasonCode)) missing.push("Rejection Reason");
    }

    if (choice === "New Proposal") {
      if (isMissing(selectedChoice?.safetyStock)) missing.push("Safety Stock");
      if (isMissing(selectedChoice?.coverageProfile)) missing.push("Coverage Profile");
      if (isMissing(selectedChoice?.safetyTime)) missing.push("Safety Time");
      if (isMissing(reasonCode)) missing.push("Rejection Reason");
    }

    if (missing.length === 0) return "";

    return `Please fill the following before submitting:\n- ${missing.join(", ")}`;
  };
  return (
    <Dialog open={ open } onClose={ onClose } maxWidth="lg" fullWidth>
      <Stack
        direction="row"
        spacing={ 8 }
        sx={ {
          // alignItems: 'center',
          // justifyContent: 'space-between',
          padding: 1,
        } }
      >
        <Typography variant="h6" textAlign="center" flex={ 1 }>
          Review Proposal for Material Number { data?.materialNumber }
        </Typography>
      </Stack>

      <DialogContent sx={ { padding: "20px" } }>
        <Stack direction="row" spacing={ 4 } sx={ { width: "100%" } }>
          {/* Left: Variation + History */ }
          <Stack
            sx={ {
              flex: 0.2,
              minWidth: 380,
              borderRight: "1px solid #ddd",
              pr: 2,
            } }
          >
            <Typography variant="h6" sx={ { mb: 2 } }>
              End User Details
            </Typography>
            <Box sx={ { display: "flex", alignItems: "center", mb: 2 } }>
              <Typography sx={ { fontWeight: "bold", mr: 1 } }>
                User Selected Choice:
              </Typography>
              <Chip
                label={ data?.userSelectedChoice }
                sx={ {
                  border: "1px solid blue",
                  color: "blue",
                  backgroundColor: "white",
                  borderRadius: "20px",
                  fontWeight: "bold",
                  padding: "4px 10px",
                  fontSize: "0.9rem",
                } }
              />
            </Box>
            <Typography>
              <strong>End User Comments:</strong>{ " " }
              { data?.userComments || "No comments" }
            </Typography>
            <Typography variant="h6" sx={ { mb: 2 } }>
              Variation Metrics
            </Typography>
            <Paper sx={ { height: "160px", width: "100%" } }>
              <div
                className="ag-theme-quartz"
                style={ { height: "100%", width: "100%" } }
              >
                <AgGridReact
                  columnDefs={ variationColumns }
                  rowData={ variationRows }
                  pagination={ false }
                  autoSizeStrategy={ { type: "fitCellContents" } }
                  theme={ gridTheme }
                  defaultColDef={ {
                    valueFormatter: ({ value }: any) =>
                      value === null ||
                        value === undefined ||
                        (typeof value === "string" && value.trim() === "") ||
                        (typeof value === "number" && Number.isNaN(value))
                        ? "-"
                        : value,
                  } }
                  gridOptions={ {
                    sideBar: { hiddenByDefault: true },
                    selectionColumnDef: {
                      maxWidth: 60,
                      cellStyle: {
                        display: "flex",
                        justifyContent: "center", // Centers radio button horizontally
                        alignItems: "center", // Centers vertically
                      },
                    },
                  } }
                  rowHeight={ 40 }
                />
              </div>
            </Paper>
            <VersionHistoryView
              id={ getDataId(data) }
              s={ {
                ...rows,
                approverId: approverId,
                expandedHistory,
                materialNumber: data?.materialNumber,
                materialPlant: data?.materialPlant,
              } }
              toggleHistory={ () => {
                setExpandedHistory(!expandedHistory);
              } }
            />
          </Stack>
          <Stack sx={ { flex: 1.9, minWidth: 550 } }>
            <Typography
              width="100%"
              variant="h6"
              sx={ {
                paddingX: "8px",
                fontWeight: "400",
                fontSize: "1.1rem",

                letterSpacing: "0.5px",
              } }
            >
              Approver Actions
            </Typography>
            <Paper sx={ { height: "240px", width: "100%" } }>
              <div
                className="ag-theme-quartz"
                style={ { height: "100%", width: "100%" } }
              >
                { " " }
                <AgGridReact
                  columnDefs={ columns }
                  rowData={ rows }
                  getRowId={ (params) => params.data.choice }
                  theme={ gridTheme }
                  pagination={ false }
                  rowHeight={ 40 }
                  autoSizeStrategy={ { type: "fitGridWidth" } }
                  onCellValueChanged={ handleCellValueChanged }
                  rowSelection="single" //  REQUIRED
                  suppressRowClickSelection={ true } //  radio-only selection
                  onFirstDataRendered={ (params) => {
                    params.api.getDisplayedRowAtIndex(0)?.setSelected(true);
                  } }
                  onRowSelected={ (event) => {
                    if (event.node.isSelected()) {
                      setSelectedChoice(event.data);
                    }
                  } }
                  gridOptions={ {
                    sideBar: { hiddenByDefault: true },
                  } }
                />
              </div>
            </Paper>

            { /* Real-time limit error messages below the grid */ }
            { (cellLimitErrors.safetyTime || cellLimitErrors.coverageProfile) && (
              <Box sx={{ mt: 0.5 }}>
                { cellLimitErrors.coverageProfile && (
                  <Typography variant="caption" color="error" display="block">
                    ⚠️ { cellLimitErrors.coverageProfile }
                  </Typography>
                ) }
                { cellLimitErrors.safetyTime && (
                  <Typography variant="caption" color="error" display="block">
                    ⚠️ { cellLimitErrors.safetyTime }
                  </Typography>
                ) }
              </Box>
            ) }

            <div style={ { marginTop: "12px" } }>
              { " " }
              <FormControl fullWidth size="small" sx={ formControlStyle }>
                <InputLabel>Rejection Reason</InputLabel>
                {/* <Select
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                disabled={selectedChoice?.choice == "Accept End User Proposal" || selectedChoice?.choice == "Accept IO value"}
              >
                <MenuItem value="reason0">Reason 0</MenuItem>
                <MenuItem value="reason1">Reason 1</MenuItem>
                <MenuItem value="reason2">Reason 2</MenuItem>
              </Select> */}

                <Select
                  value={ reasonCode }
                  onChange={ (e) => setReasonCode(e.target.value) }
                  disabled={
                    selectedChoice?.choice == "Accept End User Proposal" ||
                    selectedChoice?.choice == "Accept IO value" ||
                    reasonLoading
                  }
                  displayEmpty
                >
                  {/* Loader row */ }
                  { reasonLoading && (
                    <MenuItem disabled>
                      <Stack direction="row" spacing={ 1 } alignItems="center">
                        <CircularProgress size={ 16 } />
                        <span>Fetching reasons…</span>
                      </Stack>
                    </MenuItem>
                  ) }

                  {/* Dynamic API-based reason list */ }
                  { !reasonLoading &&
                    reasonOptions.map((opt) => (
                      <MenuItem key={ opt.value } value={ opt.value }>
                        { opt.label }
                      </MenuItem>
                    )) }

                  {/* Error fallback */ }
                  { reasonError && (
                    <MenuItem disabled>
                      <span style={ { color: "red" } }>{ reasonError }</span>
                    </MenuItem>
                  ) }
                </Select>
              </FormControl>
              {/* Approver Comments */ }
              <TextareaAutosize
                minRows={ 3 }
                placeholder="Approver Comments"
                value={ comments }
                onChange={ (e) => setComments(e.target.value) }
                style={ {
                  width: "100%",
                  marginTop: "12px",
                } }
              />
            </div>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Grid container width="100%" spacing={ 1 }>
          <Grid container size={ 5 } direction="row-reverse">
            {/* <Fab
              variant="extended"
              size="medium"
              sx={{
                backgroundColor: '#4caf50',
                color: '#fff',
                minWidth: '90px',
                height: '32px',
                mr: 1,
                '&:hover': { backgroundColor: '#388e3c', color: '#fff' }
              }}
              onClick={onApprove}
            >
              Approve
            </Fab> */}
          </Grid>
          {/* Approve Button */ }
          <Grid container size={ 7 } direction="row-reverse">
            {/* Cancel Button */ }
            <Fab
              variant="extended"
              size="medium"
              sx={ {
                backgroundColor: "#9e9e9e",
                color: "#fff",
                minWidth: "90px",
                height: "32px",
                "&:hover": { backgroundColor: "#616161", color: "#fff" },
              } }
              onClick={ onClose }
            >
              Cancel
            </Fab>
            <Tooltip
              placement="top"
              arrow
              title={ isIncomplete() ? getMissingMessage() : "" }
            >
              <span>
                <Fab
                  variant="extended"
                  size="medium"
                  sx={ {
                    backgroundColor: "transparent",
                    color: "#000",
                    minWidth: "90px",
                    height: "32px",
                    "&:hover": {
                      backgroundColor: "green",
                      color: "#fff",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#555",
                      cursor: "not-allowed",
                      opacity: 1,
                    },
                  } }
                  disabled={ submitting || isIncomplete() }
                  onClick={ handleSubmit }
                >
                  { submitting ? "Submitting..." : "Submit" }
                </Fab>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogActions>
      <ErrorDialog
        open={ errorToast.open }
        message={ errorToast.msg }
        onClose={ () => setErrorToast({ open: false, msg: "" }) }
      />
    </Dialog>
  );
};

export default RejectPopUp;


